import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { logger } from "@/lib/logger";

const MAX_POLLS = 12;
const POLL_INTERVAL = 2000;

export function useBilling() {
  const [params, setParams] = useSearchParams();
  const sessionId = params.get("session_id");

  const [sub, setSub] = useState(null);
  const [config, setConfig] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState("");
  const [verifying, setVerifying] = useState(!!sessionId);

  const loadAll = useCallback(() => {
    api.get("/subscription").then(({ data }) => setSub(data)).catch((e) => logger.warn("load subscription", e));
    api.get("/config").then(({ data }) => setConfig(data)).catch((e) => logger.warn("load config", e));
    api.get("/billing/history").then(({ data }) => setHistory(data.transactions)).catch((e) => logger.warn("load history", e));
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const finishVerify = useCallback(() => {
    setVerifying(false);
    setParams({}, { replace: true });
  }, [setParams]);

  const verifyPayment = useCallback(() => {
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          toast.success("Payment successful! Your account is updated.");
          finishVerify();
          loadAll();
          return;
        }
        if (data.status === "expired") {
          toast.error("Payment session expired.");
          finishVerify();
          return;
        }
      } catch (e) {
        logger.warn("payment status poll failed; retrying", e);
      }
      attempts += 1;
      if (attempts >= MAX_POLLS) {
        finishVerify();
        return;
      }
      setTimeout(poll, POLL_INTERVAL);
    };
    poll();
  }, [sessionId, finishVerify, loadAll]);

  useEffect(() => {
    if (sessionId) verifyPayment();
  }, [sessionId, verifyPayment]);

  const act = useCallback(async (fn, key) => {
    setBusy(key);
    try {
      await fn();
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy("");
    }
  }, [loadAll]);

  const cancel = useCallback(() => act(async () => {
    await api.post("/subscription/cancel");
    toast.success("Subscription canceled. Access continues until period end.");
  }, "cancel"), [act]);

  const resume = useCallback(() => act(async () => {
    await api.post("/subscription/resume");
    toast.success("Subscription resumed.");
  }, "resume"), [act]);

  const downgradeFree = useCallback(() => act(async () => {
    await api.post("/subscription/downgrade-free");
    toast.success("Switched to Free plan.");
  }, "free"), [act]);

  const redirectToCheckout = useCallback(async (path, body, key) => {
    setBusy(key);
    try {
      const { data } = await api.post(path, body);
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      setBusy("");
    }
  }, []);

  const updatePayment = useCallback(() => {
    // Payment method changes are handled via the Stripe billing portal.
    redirectToCheckout("/billing/portal", { origin_url: window.location.origin }, "portal");
  }, [redirectToCheckout]);

  const buyAddon = useCallback((addonId) => {
    redirectToCheckout("/checkout/addon", { addon_id: addonId, origin_url: window.location.origin }, addonId);
  }, [redirectToCheckout]);

  const cancelAddon = useCallback((addonId) => act(async () => {
    await api.post("/subscription/addon/cancel", { addon_id: addonId });
    toast.success("Listing add-on canceled. Placement continues until the period ends.");
  }, `cancel-${addonId}`), [act]);

  const openPortal = useCallback(() => {
    redirectToCheckout("/billing/portal", { origin_url: window.location.origin }, "portal");
  }, [redirectToCheckout]);

  return { sub, config, history, busy, verifying, cancel, resume, downgradeFree, updatePayment, buyAddon, cancelAddon, openPortal };
}
